import { PosProvider, usePosContext } from "./context/pos-context";
import { PosHeader } from "./components/pos-header";
import { PosCategorySidebar } from "./components/pos-category-sidebar";
import { PosProductGrid } from "./components/pos-product-grid";
import { PosCartBar } from "./components/pos-cart-bar";
import { PosCartView } from "./components/pos-cart-view";
import { PosOrderHistory } from "./components/pos-order-history";
import { PosPaymentSelection } from "./components/pos-payment-selection";
import { PosVariantSelectorDialog } from "./components/pos-variant-selector-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { CheckCircle2 } from "lucide-react";

export default function PosPage() {
  return (
    <PosProvider>
      <PosPageContent />
    </PosProvider>
  );
}

function PosPageContent() {
  const { t } = useTranslation("pos");
  const {
    screen,
    setScreen,
    isDesktop,
    isCartOpen,
    setIsCartOpen,
    isOrderHistoryOpen,
    setIsOrderHistoryOpen,
    selectedPaymentId,
    setSelectedPaymentId,
    handlePaymentConfirm,
  } = usePosContext();

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      <PosHeader />

      <Sheet open={isOrderHistoryOpen} onOpenChange={setIsOrderHistoryOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-[400px] p-0 flex flex-col"
        >
          <SheetHeader className="px-4 py-3 border-b">
            <SheetTitle>{t("recentOrders")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto p-4">
            <PosOrderHistory />
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {screen === "menu" && (
          <>
            <div
              className={cn(
                "flex-1 flex min-h-0 overflow-hidden max-w-6xl mx-auto",
                !isDesktop && "pb-24"
              )}
            >
              {/* <PosCategorySidebar /> */}
              <div className="flex-1 min-h-0 overflow-y-auto min-w-0">
                <PosProductGrid />
              </div>
            </div>

            {isDesktop && (
              <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
                <SheetContent
                  side="right"
                  className="w-full sm:max-w-[400px] p-0 flex flex-col min-h-0 overflow-hidden"
                >
                  <SheetHeader className="px-4 py-3 border-b">
                    <SheetTitle>{t("yourCart")}</SheetTitle>
                  </SheetHeader>
                  <PosCartView />
                </SheetContent>
              </Sheet>
            )}
          </>
        )}

        {screen === "cart" && (
          <div className="flex-1 flex flex-col min-h-0">
            <PosCartView />
          </div>
        )}

        {screen === "payment" && (
          <div className="flex-1 flex flex-col min-h-0">
            <PosPaymentSelection />
            <div className="border-t bg-white p-4 flex-shrink-0">
              <div className="max-w-md mx-auto flex gap-3">
                <button
                  type="button"
                  onClick={() => setScreen("cart")}
                  className="flex-1 px-5 py-3 rounded-xl border font-medium hover:bg-gray-50"
                >
                  {t("back")}
                </button>
                <button
                  type="button"
                  onClick={handlePaymentConfirm}
                  disabled={!selectedPaymentId}
                  className={cn(
                    "flex-1 px-5 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:bg-primary/90",
                    !selectedPaymentId && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {t("confirmPayment")}
                </button>
              </div>
            </div>
          </div>
        )}

        {screen === "confirmation" && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 animate-in fade-in zoom-in-95 duration-300">
            <div className="rounded-full bg-green-100 p-6 mb-6">
              <CheckCircle2 className="h-16 w-16 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              {t("orderConfirmed")}
            </h2>
            <p className="text-muted-foreground text-center mb-8 max-w-sm">
              {t("thankYouMessage")}
            </p>
            <button
              type="button"
              onClick={() => {
                setScreen("menu");
                setSelectedPaymentId(null);
              }}
              className="px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-lg hover:bg-primary/90 transition-colors"
            >
              {t("newOrder")}
            </button>
          </div>
        )}
      </div>

      {screen === "menu" && !isDesktop && (
        <div className="fixed bottom-0 left-0 right-0 z-10 pb-[env(safe-area-inset-bottom,0px)]">
          <PosCartBar />
        </div>
      )}

      <PosVariantSelectorDialog />
    </div>
  );
}
